import React, { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Float, Lightformer, Sparkles as DreiSparkles } from "@react-three/drei";
import { motion, useInView, useScroll, useSpring, useTransform } from "framer-motion";
import { useSpring as useSpringReact, animated } from "@react-spring/web";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Gem, Globe2, Sparkles, Workflow } from "lucide-react";
import QuoteFloralScene from "./QuoteFloralScene";

gsap.registerPlugin(ScrollTrigger);

// 3D hero sculpture reacting to cursor movement
const HeroSculpture = ({ pointer }) => {
  const groupRef = useRef(null);

  useFrame((state) => {
    if (!groupRef.current) return;
    const targetX = pointer.current?.x ?? 0;
    const targetY = pointer.current?.y ?? 0;

    groupRef.current.rotation.y = THREE.MathUtils.lerp(
      groupRef.current.rotation.y,
      targetX * 0.55,
      0.05
    );
    groupRef.current.rotation.x = THREE.MathUtils.lerp(
      groupRef.current.rotation.x,
      -targetY * 0.35,
      0.05
    );
  });

  return (
    <group ref={groupRef}>
      <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.5}>
        <mesh position={[0, 0.57, 0]}>
          <icosahedronGeometry args={[0.85, 1]} />
          <meshStandardMaterial color="#ffb6d9" metalness={0.95} roughness={0.18} />
        </mesh>
      </Float>
      <Float speed={2.5} rotationIntensity={0.4} floatIntensity={0.4}>
        <mesh position={[1.7, 1.35, -1.3]}>
          <torusKnotGeometry args={[0.3, 0.09, 100, 20]} />
          <meshStandardMaterial color="#ffc9e3" metalness={0.85} roughness={0.2} />
        </mesh>
      </Float>
      <Float speed={3} rotationIntensity={0.3} floatIntensity={0.3}>
        <mesh position={[-1.35, 2.35, -0.3]}>
          <dodecahedronGeometry args={[0.4]} />
          <meshStandardMaterial color="#ffd4ed" metalness={0.75} roughness={0.24} />
        </mesh>
      </Float>
    </group>
  );
};

// Interactive Hobbies Circle Component
const HobbiesCircle = ({ passions, nightMode, onHoverChange }) => {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  const handleHover = (index) => {
    setHoveredIndex(index);
    if (onHoverChange) onHoverChange(index);
  };

  const radius = 180; // Circle radius
  const dotRadius = 230; // Dot position radius (outside the circle)
  const dotCount = passions.length;

  // Calculate dot positions around the circle (outside)
  const getDotPosition = (index) => {
    const angle = (index * 360) / dotCount - 90; // Start from top
    const radian = (angle * Math.PI) / 180;
    return {
      x: dotRadius * Math.cos(radian),
      y: dotRadius * Math.sin(radian),
    };
  };

  // Calculate arc segments for the circle
  const getArcPath = (index) => {
    const segmentAngle = 360 / dotCount;
    const gapAngle = 15; // Gap between segments in degrees
    const arcAngle = segmentAngle - gapAngle;

    const startAngle = (index * segmentAngle - 90) + (gapAngle / 2);
    const endAngle = startAngle + arcAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = radius * Math.cos(startRad);
    const y1 = radius * Math.sin(startRad);
    const x2 = radius * Math.cos(endRad);
    const y2 = radius * Math.sin(endRad);

    const largeArcFlag = arcAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  return (
    <motion.div
      className="hobbies-circle-container"
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Hovered Hobby Title - Top Left near circle */}
      {hoveredIndex !== null && (
        <motion.div
          className="hobbies-circle__title-floating"
          initial={{ opacity: 0, x: -30, y: 30 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: 'absolute',
            top: '35%',
            left: '-15%',
            maxWidth: '280px',
            zIndex: 10,
          }}
        >
          <h2 className="hobbies-circle__heading">
            {passions[hoveredIndex].title}
          </h2>
        </motion.div>
      )}

      {/* Hovered Hobby Description - Bottom Right near circle */}
      {hoveredIndex !== null && (
        <motion.div
          className="hobbies-circle__description-floating"
          initial={{ opacity: 0, x: 30, y: -30 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '-55%',
            maxWidth: '480px',
            textAlign: 'right',
            zIndex: 10,
          }}
        >
          <p className="hobbies-circle__description">
            {passions[hoveredIndex].copy}
          </p>
        </motion.div>
      )}

      <div className="hobbies-circle">
        {/* Circle SVG with Arc Segments */}
        <svg className="hobbies-circle__svg" viewBox="-250 -250 500 500">
          {/* Segmented Circle Arcs */}
          {passions.map((_, index) => (
            <path
              key={index}
              d={getArcPath(index)}
              fill="none"
              stroke="currentColor"
              strokeWidth="0.5"
              opacity="0.2"
            />
          ))}
        </svg>

        {/* Center Text */}
        <motion.div
          className="hobbies-circle__center"
          animate={{
            opacity: hoveredIndex === null ? 1 : 0,
            scale: hoveredIndex === null ? 1 : 0.8,
          }}
          transition={{ duration: 0.3 }}
        >
          <span className="hobbies-circle__label">Outside the atelier</span>
          <h3 className="hobbies-circle__title">HOBBIES</h3>
        </motion.div>

        {/* Center Image - appears on hover */}
        {hoveredIndex !== null && (
          <motion.div
            className="hobbies-circle__center-image"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={passions[hoveredIndex].image}
              alt={passions[hoveredIndex].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%'
              }}
            />
          </motion.div>
        )}

        {/* Interactive Dots with Large Hitbox */}
        {passions.map((passion, index) => {
          const pos = getDotPosition(index);
          return (
            <motion.div
              key={index}
              className={`hobbies-circle__dot ${hoveredIndex === index ? 'active' : ''}`}
              style={{
                left: `calc(50% + ${pos.x}px)`,
                top: `calc(50% + ${pos.y}px)`,
                '--dot-color': passion.accent,
              }}
              onMouseEnter={() => handleHover(index)}
              onMouseLeave={() => handleHover(null)}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.5 }}
            >
              {/* Invisible larger hitbox for easier hovering */}
              <div className="hobbies-circle__dot-hitbox" />
              <div className="hobbies-circle__dot-inner" />
              <motion.div
                className="hobbies-circle__dot-ring"
                animate={{
                  scale: hoveredIndex === index ? 2 : 1,
                  opacity: hoveredIndex === index ? 0.6 : 0,
                }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

// Scroll-Pinned Pillars Section - Velvele Studio Style
const PillarExperience = ({ pillars, nightMode }) => {
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = React.useState(0);

  useEffect(() => {
    if (!sectionRef.current) return;

    const section = sectionRef.current;
    const isMobile = window.innerWidth <= 768;

    // Skip scroll-pin on mobile devices
    if (isMobile) {
      // Show all items on mobile
      setActiveIndex(0);
      return;
    }

    // Pin section and create progress-based animations (desktop only)
    ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: `+=${window.innerHeight * (pillars.length - 1)}`,
      pin: true,
      pinSpacing: true,
      onUpdate: (self) => {
        const newIndex = Math.min(
          Math.floor(self.progress * pillars.length),
          pillars.length - 1
        );
        setActiveIndex(newIndex);
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars.trigger === section) st.kill();
      });
    };
  }, [pillars.length]);

  return (
    <section className="pillars-scrollpin" ref={sectionRef}>
      {/* Left Column - Single Active Number */}
      <div className="pillars-scrollpin__left">
        <div className="pillars-scrollpin__eyebrow">WHAT I BUILD AROUND</div>
        <div className="pillars-scrollpin__numbers">
          <div className="pillars-scrollpin__number active">
            {String(activeIndex + 1).padStart(2, '0')}
          </div>
        </div>
      </div>

      {/* Right Column - Content */}
      <div className="pillars-scrollpin__right">
        {pillars.map(({ title, detail, icon: Icon, accent, tone, tag, outputs }, index) => (
          <div
            key={index}
            className={`pillars-scrollpin__item ${activeIndex === index ? 'active' : ''}`}
            style={{
              '--card-accent': tone,
              '--card-soft': accent,
            }}
          >
            <div className="pillars-scrollpin__content">
              {/* Icon */}
              <div className="pillars-scrollpin__icon-wrapper">
                <div className="pillars-scrollpin__icon">
                  <Icon size={24} strokeWidth={1.5} />
                </div>
              </div>

              {/* Tag */}
              <span className="pillars-scrollpin__tag">{tag}</span>

              {/* Title */}
              <h3 className="pillars-scrollpin__title">{title}</h3>

              {/* Detail */}
              <p className="pillars-scrollpin__detail">{detail}</p>

              {/* Outputs */}
              <div className="pillars-scrollpin__outputs">
                {outputs.map((output) => (
                  <span key={output} className="pillars-scrollpin__output">
                    {output}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const AboutSection = ({ nightMode = false }) => {
  const sectionRef = useRef(null);
  const heroRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const [hoveredHobbyIndex, setHoveredHobbyIndex] = React.useState(null);
  const [isMobile, setIsMobile] = React.useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start center", "end start"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 32,
    mass: 0.8,
  });

  const heroScale = useTransform(smoothProgress, [0, 0.6], [1, 0.9]);
  const heroOpacity = useTransform(smoothProgress, [0, 0.7], [1, 0.4]);
  const heroY = useTransform(smoothProgress, [0, 1], [0, -160]);

  const heroInView = useInView(heroRef, { once: false, amount: 0.45 });

  const [glowSpring, glowApi] = useSpringReact(() => ({
    xy: [0, 0],
    config: { mass: 1, tension: 220, friction: 30 },
  }));

  const chips = [
    "Luxury Buying & Merchandising Strategy",
    "Brand Positioning & Storytelling",
    "Consumer Insight & Market Research",
    "Cultural Foresight & Experience Design",
  ];

  const portraitImageSrc = "/dp.jpg";

  const passions = [
    {
      badge: "Visual craft",
      title: "Painting",
      highlight: "Canvas work • Old masters • Intent & beauty",
      copy:
        "Colour behaves like language, every shade says something if you know how to listen. Studying the works of old masters made me appreciate how intent hides inside beauty. Painting became a way to practice that precision, one quiet layer at a time.",
      accent: "rgba(255, 184, 210, 0.45)",
      image: "/Projects/hobbies/Painting.png",
      bgColor: "#fdfcfb",
      textColor: "#1f1f24",
    },
    {
      badge: "Visual storytelling",
      title: "Photography",
      highlight: "Composition • Light & timing",
      copy:
        "Photography slows everything down. It makes you notice light, timing, and stories that exist between moments. I studied it for a year in college, and that time trained my eye to find composition and emotion even in ordinary spaces.",
      accent: "rgba(255, 220, 230, 0.44)",
      image: "/Projects/hobbies/Photography.png",
      bgColor: "#e8e8e8",
      textColor: "#1f1f24",
    },
    {
      badge: "Narrative fuel",
      title: "Reading",
      highlight: "Novels • Fashion history • Cultural studies",
      copy:
        "Reading is how I understand movement, of ideas, of people, of culture. From novels to fashion history and cultural studies, everything connects through context. It keeps me curious and helps me see how taste evolves before trends catch up.",
      accent: "rgba(186, 200, 255, 0.42)",
      image: "/Projects/hobbies/Reading.png",
      bgColor: "#f8f8f8",
      textColor: "#1f1f24",
    },
    {
      badge: "Sensory craft",
      title: "Cooking",
      highlight: "Indian cuisine • Fusion • Improvisation",
      copy:
        "Cooking is precision disguised as play. I know my way around Indian dishes, but what keeps me interested is everything I don't know — new cuisines, unexpected fusions, rules that beg to be bent. It's a small exercise in timing, instinct, and knowing exactly when to improvise.",
      accent: "rgba(255, 205, 196, 0.5)",
      image: "/Projects/hobbies/Cooking.png",
      bgColor: "#ffffff",
      textColor: "#1f1f24",
    },
    {
      badge: "Movement & rhythm",
      title: "Sports",
      highlight: "Pistol shooting • Badminton • Skating • Woodball",
      copy:
        "Sports teach precision and rhythm in their own ways. Pistol shooting builds focus, skating builds flow, and badminton keeps both sharp. Woodball, where I earned a national title, taught me foresight—the art of reading the entire field before making a single move.",
      accent: "rgba(255, 219, 186, 0.46)",
      image: "/Projects/hobbies/Sports.png",
      bgColor: "#ffffff",
      textColor: "#1f1f24",
    },
    {
      badge: "Creative direction",
      title: "Styling",
      highlight: "Visual editing • Texture & silhouette",
      copy:
        "Styling is the art of visual editing. It's creating a narrative through texture, silhouette, and proportion. For me, it's less about perfection and more about the discipline of knowing what to remove, what to emphasize, and how to arrange elements so the final result feels both intentional and completely effortless.",
      accent: "rgba(230, 210, 255, 0.43)",
      image: "/Projects/hobbies/Styling.png",
      bgColor: "#fefefe",
      textColor: "#1f1f24",
    },
    {
      badge: "Conceptual thinking",
      title: "Ideation",
      highlight: "Fragments • Structured chaos • Growth",
      copy:
        "Ideas start as fragments, notes, visuals, thoughts that don't yet belong together. They build into something whole only when they're given space to grow. For me, ideation is structured chaos, the part of the process that keeps everything alive.",
      accent: "rgba(200, 228, 211, 0.48)",
      image: "/Projects/hobbies/Ideation.png",
      bgColor: "#ffffff",
      textColor: "#1f1f24",
    },
  ];

  const pillars = [
    {
      title: "Cultural Foresight Systems",
      detail:
        "Decode global signals, collector behaviour, and heritage codes so maisons enter every market already fluent.",
      icon: Globe2,
      accent: "rgba(255, 210, 235, 0.38)",
      tone: "#e497b5",
      tag: "Signal cartography",
      outputs: ["Cultural Heatmaps", "Market Ritual Playbooks"],
    },
    {
      title: "Narrative Immersion Engines",
      detail:
        "Design cinematic story arcs that connect runway, retail, and digital touchpoints with one emotional through line.",
      icon: Sparkles,
      accent: "rgba(255, 210, 235, 0.4)",
      tone: "#f8a5c2",
      tag: "Storycraft",
      outputs: ["Launch Arcs", "Flagship Scenography"],
    },
    {
      title: "Merchandising Intelligence Lab",
      detail:
        "Fuse demand data with creative direction to engineer assortments that feel inevitable and sell-through ready.",
      icon: Workflow,
      accent: "rgba(255, 210, 235, 0.42)",
      tone: "#d68fb8",
      tag: "Commerce design",
      outputs: ["Dynamic OTB Models", "Assortment Blueprints"],
    },
    {
      title: "Collector Ecosystem Design",
      detail:
        "Build private client rituals and membership worlds that deepen devotion long after a launch.",
      icon: Gem,
      accent: "rgba(255, 210, 235, 0.46)",
      tone: "#f0b5d0",
      tag: "Loyalty alchemy",
      outputs: ["Clienteling Journeys", "Experiential Memberships"],
    },
  ];


  useEffect(() => {
    if (typeof window === "undefined") return;

    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    const handlePointerMove = (event) => {
      const { clientX, clientY } = event;
      pointerRef.current.x = (clientX / window.innerWidth - 0.5) * 2;
      pointerRef.current.y = (clientY / window.innerHeight - 0.5) * 2;
      glowApi.start({ xy: [clientX, clientY] });
    };

    glowApi.start({
      xy: [window.innerWidth / 2, window.innerHeight / 2],
      immediate: true,
    });

    window.addEventListener("pointermove", handlePointerMove);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener('resize', checkMobile);
    };
  }, [glowApi]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".about-immersive__portrait-frame", {
        scrollTrigger: {
          trigger: ".about-immersive__portrait-frame",
          start: "top 85%",
        },
        opacity: 0,
        y: 48,
        duration: 1,
        ease: "power3.out",
      });

      gsap.from(".pillar-experience__canvas", {
        scrollTrigger: {
          trigger: ".pillar-experience",
          start: "top 82%",
        },
        opacity: 0,
        y: 60,
        duration: 1.1,
        ease: "power3.out",
      });

      gsap.from(".pillar-experience__card", {
        scrollTrigger: {
          trigger: ".pillar-experience__cards",
          start: "top 80%",
        },
        opacity: 0,
        y: 60,
        stagger: 0.14,
        duration: 1,
        ease: "power3.out",
      });

      gsap.from(".about-immersive__ethos-item", {
        scrollTrigger: {
          trigger: ".about-immersive__ethos",
          start: "top 75%",
        },
        opacity: 0,
        x: -40,
        stagger: 0.18,
        duration: 0.9,
        ease: "power2.out",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const sectionClassName = `about-immersive ${nightMode ? "about-immersive--dark" : "about-immersive--light"}`;

  const sectionBgColor = hoveredHobbyIndex !== null ? passions[hoveredHobbyIndex].bgColor : null;
  const sectionTextColor = hoveredHobbyIndex !== null ? passions[hoveredHobbyIndex].textColor : null;

  return (
    <section
      id="about"
      className={sectionClassName}
      ref={sectionRef}
      style={{
        '--dynamic-text-color': sectionTextColor || 'var(--about-text-primary)'
      }}
    >
      {/* Animated background overlay */}
      <motion.div
        className="about-immersive__bg-overlay"
        animate={{
          opacity: sectionBgColor ? 0.85 : 0,
          backgroundColor: sectionBgColor || 'transparent'
        }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}
      />
      <div className="about-immersive__backdrop">
        <animated.div
          className="about-immersive__glow"
          style={{ transform: glowSpring.xy.to((x, y) => `translate3d(${x - 220}px, ${y - 220}px, 0)`) }}
        />
        <div className="about-immersive__gradient" />
        <Suspense fallback={null}>
          <Canvas
            className="about-immersive__canvas"
            camera={{ position: [0, 0, 4], fov: 42 }}
            dpr={[1, 1.5]}
            frameloop="always"
            gl={{
              antialias: true,
              powerPreference: "high-performance",
              alpha: true,
              stencil: false,
            }}
          >
            <ambientLight intensity={0.6} />
            <directionalLight position={[4, 4, 6]} intensity={0.6} />
            <spotLight position={[-5, 8, 6]} intensity={1.4} angle={0.4} penumbra={0.6} />
            <HeroSculpture pointer={pointerRef} />
            <Environment preset="city" />
          </Canvas>
        </Suspense>
        <div className="about-immersive__overlay" />
      </div>

      <div className="about-immersive__container">
        <motion.div
          className="about-immersive__hero"
          ref={heroRef}
          style={{ scale: heroScale, opacity: heroOpacity, y: heroY }}
        >
          <div className="about-immersive__hero-copy">
            <motion.span
              className="about-immersive__label"
              initial={{ opacity: 0, x: -20 }}
              animate={heroInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              ABOUT
            </motion.span>

            <motion.h1 className="about-immersive__title">
              <motion.span
                className="about-immersive__title-line"
                initial={{ opacity: 0, y: 60 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                luxury with a
              </motion.span>
              <motion.span
                className="about-immersive__title-line about-immersive__title-line--accent"
                initial={{ opacity: 0, y: 60 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                transition={{ duration: 0.8, delay: 0.35 }}
              >
                lowercase l.
              </motion.span>
            </motion.h1>

            <motion.p
              className="about-immersive__intro"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7, delay: 0.45 }}
            >
              I'm someone who reads brand reports like novels and novels like brand reports. I see stories in packaging, poetry in retail, and culture hidden in campaigns. I like to think of myself as fluent in two languages: strategy and sentiment.
One is structured, built from metrics, market insights, and brand decks. The other is instinctive - colour palettes, human behaviour, and the reasons we fall in love with a product. My work lives in the space between the two; decoding how culture, desire, and detail create what we call luxury.
            </motion.p>

            <motion.p
              className="about-immersive__intro about-immersive__intro--note"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.7, delay: 0.55 }}
            >
              <em>
                lowercase, because to me, luxury isn't just louder, higher, or rarer, it's quieter,
                human, and meant to be understood.
              </em>
            </motion.p>

            <div className="about-immersive__chips">
              {chips.map((chip) => (
                <motion.span
                  key={chip}
                  className="about-immersive__chip"
                  initial={{ opacity: 0, y: 16 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5 }}
                >
                  {chip}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="about-immersive__hero-panel">
            <div className="about-immersive__portrait-frame">
              <img
                src={portraitImageSrc}
                alt="Portrait of Surmayee, luxury strategist"
                className="about-immersive__portrait"
                loading="lazy"
              />
              <div className="about-immersive__portrait-identity">
                <span className="about-immersive__portrait-name">Surmayee Umathe</span>
                <span className="about-immersive__portrait-title">
                  Luxury Management & Marketing Expert
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <PillarExperience pillars={pillars} nightMode={nightMode} />

      <div className="about-immersive__container">
        {!isMobile ? (
          <HobbiesCircle passions={passions} nightMode={nightMode} onHoverChange={setHoveredHobbyIndex} />
        ) : (
          <div className="hobbies-mobile-container">
            <div className="hobbies-mobile-header">
              <span className="hobbies-mobile-label">Outside the atelier</span>
              <h3 className="hobbies-mobile-title">HOBBIES</h3>
            </div>
            <div className="hobbies-mobile-scroll">
              {passions.map((passion, index) => (
                <motion.div
                  key={index}
                  className="hobby-mobile-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="hobby-mobile-image">
                    <img src={passion.image} alt={passion.title} />
                  </div>
                  <div className="hobby-mobile-content">
                    <span className="hobby-mobile-badge">{passion.badge}</span>
                    <h4 className="hobby-mobile-title-text">{passion.title}</h4>
                    <p className="hobby-mobile-highlight">{passion.highlight}</p>
                    <p className="hobby-mobile-description">{passion.copy}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="about-immersive__quote-wrapper">
          <motion.blockquote
            className="about-immersive__quote"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
          >
            <span className="about-immersive__quote-mark">"</span>
            <p>
              Luxury isn't about owning more, it's about noticing better.
            </p>
            <span className="about-immersive__quote-mark about-immersive__quote-mark--close">"</span>
          </motion.blockquote>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
